import base from './base';
import { themes } from './index';

export interface ITheme {
  [key: string]: any;
}
export interface IThemes {
  [key: string]: ITheme;
}
export interface IMappedTheme {
  [key: string]: string | null;
}

export const mapTheme = (variables: ITheme): IMappedTheme => {
  return {
    'background-color': variables.neutral?.[50] || base.neutral[50],
    '--border': variables?.borderColor || base.neutral[200],
    // ---------------------
    // Primary Colors
    // ---------------------
    '--color-primary': variables?.primary.DEFAULT || base.primary.DEFAULT,
    '--color-primary-text': variables?.primary.text || base.primary.text,
    '--color-primary-bg': variables?.primary.bg || base.primary.bg,
    '--color-primary-hover': variables?.primary.hover || base.primary.hover,
    '--color-primary-lighter':
      variables?.primary.lighter || base.primary.lighter,
    '--color-primary-darker': variables?.primary.darker || base.primary.darker,
    // ---------------------
    // Tertiary Colors
    // ---------------------
    '--color-tertiary': variables?.tertiary.DEFAULT || base.tertiary.DEFAULT,
    '--color-tertiary-text': variables?.tertiary.text || base.tertiary.text,
    '--color-tertiary-bg': variables?.tertiary.bg || base.tertiary.bg,
    '--color-tertiary-hover': variables?.tertiary.hover || base.tertiary.hover,
    // ---------------------
    // Secondary Colors
    // ---------------------
    '--color-secondary': variables?.secondary.DEFAULT || base.secondary.DEFAULT,
    '--color-secondary-bg': variables?.secondary.bg || base.secondary.bg,
    '--color-secondary-text': variables?.secondary.text || base.secondary.text,
    '--color-secondary-hover':
      variables?.secondary.hover || base.secondary.hover,
    // ---------------------
    // General Colors
    // ---------------------
    '--color-brand': variables?.brand || base.brand,
    '--color-highlight': variables?.highlight || base.highlight,
    '--color-disabled': base.disabled,
    '--color-event-past': base.event.past.DEFAULT,
    '--color-hyperlink': variables?.hyperlink || base.hyperlink,
    '--color-input': variables?.input || base.input,
    '--color-background': variables?.background || base.background,
    '--color-overlay': variables?.overlay || base.overlay,
    // ---------------------
    // Status Colors
    // ---------------------
    '--color-success': variables?.success?.DEFAULT || base.success.DEFAULT,
    '--color-success-text': variables?.success?.text || base.success.text,
    '--color-success-bg': variables?.success?.bg || base.success.bg,
    // '--color-warning': variables?.warning?.DEFAULT || base.warning.DEFAULT,
    '--color-error': variables?.error?.DEFAULT || base.error.DEFAULT,
    '--color-error-text': variables?.error?.text || base.error.text,
    '--color-error-bg': variables?.error?.bg || base.error.bg,

    // ---------------------
    // Neutral Colors
    // ---------------------

    '--color-neutral-bg': base.neutral.bg,
    '--color-neutral-50': base.neutral[50],
    '--color-neutral-100': base.neutral[100],
    '--color-neutral-200': base.neutral[200],
    '--color-neutral-300': base.neutral[300],
    '--color-neutral-400': base.neutral[400],
    '--color-neutral-500': base.neutral[500],
    '--color-neutral-600': base.neutral[600],
    '--color-neutral-700': base.neutral[700],
    '--color-neutral-800': base.neutral[800],
    '--color-neutral-900': base.neutral[900],
    '--color-neutral-1000': base.neutral[1000],

    // ---------------------
    // Primary Colors
    // ---------------------

    '--color-primary-100': base.primary[100],
    '--color-primary-200': base.primary[200],
    '--color-primary-300': base.primary[300],
    '--color-primary-400': base.primary[400],
    '--color-primary-500': base.primary[500],
    '--color-primary-600': base.primary[600],
    '--color-primary-700': base.primary[700],
    '--color-primary-800': base.primary[800],
    '--color-primary-900': base.primary[900],
    '--color-primary-1000': base.primary[1000],

    // ---------------------
    // Specific Colors
    // ---------------------

    // ---------------------
    // Map Colors
    // ---------------------

    '--map-available-color':
      variables?.map?.availableColor ?? base.map.availableColor,
    '--map-not-available-color':
      variables?.map?.notAvailableColor ?? base.map.notAvailableColor,
    '--map-spot-not-bookeable-unavailable-stroke':
      variables?.map?.spotNotBookeableUnavailableStroke ??
      base.map.spotNotBookeableUnavailableStroke,
    '--map-spot-not-bookeable-unavailable-fill':
      variables?.map?.spotNotBookeableUnavailableFill ??
      base.map.spotNotBookeableUnavailableFill,
    '--map-spot-not-bookeable-stroke':
      variables?.map?.spotNotBookeableStroke ?? base.map.spotNotBookeableStroke,
    '--map-spot-not-bookeable-fill':
      variables?.map?.spotNotBookeableFill ?? base.map.spotNotBookeableFill,
    '--map-spot-requires-approval-stroke':
      variables?.map?.spotRequiresApprovalStroke ??
      base.map.spotRequiresApprovalStroke,
    '--map-spot-requires-approval-fill':
      variables?.map?.spotRequiresApprovalFill ??
      base.map.spotRequiresApprovalFill,
    '--map-spot-bookeable-with-schedules-stroke':
      variables?.map?.spotBookeableWithSchedulesStroke ??
      base.map.spotBookeableWithSchedulesStroke,
    '--map-spot-fully-available-stroke':
      variables?.map?.spotFullyAvailableStroke ??
      base.map.spotFullyAvailableStroke,
    '--map-spot-not-assigned-fill':
      variables?.map?.spotNotAssignedFill ?? base.map.spotNotAssignedFill,

    '--hint-bg-color': variables?.hint?.bg ?? base.hint.bg,
    '--hint-text-color': variables?.hint?.text ?? base.hint.text,

    // --------------------- RADIX TOKENS ---------------------
    // ---- if needed, radix tokens like --accent can be added here
  };
};

export const flushTheme = (themeObject: IMappedTheme, container: HTMLElement = document.documentElement): void => {
  Object.keys(themeObject).forEach((property) => {
    if (property !== 'name') {
      container.style.removeProperty(property);
    }
  });
};

export const applyTheme = (theme: string, ds: string): void => {
  const themeObject: IMappedTheme = mapTheme(themes[theme]);
  if (!themeObject || typeof window === 'undefined') return;

  if(ds === 'v1') {
    return;
  }

  const root = document.documentElement;
  flushTheme(themeObject, root);

  Object.keys(themeObject).forEach((property) => {
    if (property === 'name') {
      return;
    }
    root.style.setProperty(property, themeObject[property]);
  });
};

export const extend = (extending: ITheme, newTheme: ITheme): ITheme => {
  return { ...extending, ...newTheme };
};
